import { useEffect, useReducer } from "react"
import { useNavigate } from "react-router-dom"
import { useOutletContext } from "react-router-dom"
import { BarLoader, ClipLoader } from "react-spinners"
import { Save, Trash } from "lucide-react"

import { useGetProfile, useUpdateProfile, useProfileUploadPicture, useProfileDeletePicture } from "@/services/userService"
import { apiURL } from "@/services/apiService"
import { isAxiosError } from "axios";
import { axiosErrorToStringError } from "@/utils/axiosErrorToStringError";

import { toInitials } from "@/utils/toInitials"

/* DayJs */
import dayjs from "dayjs"
import relativeTime from "dayjs/plugin/relativeTime"
import localizedFormat from "dayjs/plugin/localizedFormat"
import "dayjs/locale/fr"

dayjs.locale("fr")
dayjs.extend(relativeTime)
dayjs.extend(localizedFormat)



const initialState = {
    initialApiPayload: {
        first_name: "",
        last_name: "",
        email: "",
        email_digest: "",
        password: "",
        password_confirm: "",
        digest_frequency: "hourly",
        digest_hour: 0,
        digest_day: 0,
        digest_include_viewed: true,
        whoiswho_hide_picture: false,
        whoiswho_hide_email: false
    },
    apiPayload: {
        first_name: "",
        last_name: "",
        email: "",
        email_digest: "",
        password: "",
        password_confirm: "",
        digest_frequency: "hourly",
        digest_hour: 0,
        digest_day: 0,
        digest_include_viewed: true,
        whoiswho_hide_picture: false,
        whoiswho_hide_email: false
    },
    profile: null,
    errorMessage: "",
    isLoading: false,
    isPictureUploadLoading: false,
}



const ACTIONS = {
    SET_INITIAL_API_PAYLOAD: 'setInitialApiPayload',
    SET_API_PAYLOAD_FIELD: 'setApiPayloadField',
    SET_ERROR_MESSAGE: 'setErrorMessage',
    SET_IS_LOADING: 'setIsLoading',
    SET_IS_PICTURE_UPLOAD_LOADING: 'setPictureUploadIsLoading',
    SET_PROFILE: 'setProfile',
}



const reducer = (state, action) => {
    switch (action.type) {
        case ACTIONS.SET_INITIAL_API_PAYLOAD:
            return { ...state, apiPayload: action.payload, initialApiPayload: action.payload, errorMessage: null }
        case ACTIONS.SET_API_PAYLOAD_FIELD:
            return { ...state, apiPayload: { ...state.apiPayload, [action.subtype]: action.payload }, errorMessage: null }
        case ACTIONS.SET_ERROR_MESSAGE:
            return { ...state, errorMessage: action.payload }
        case ACTIONS.SET_IS_LOADING:
            return { ...state, isLoading: action.payload }
        case ACTIONS.SET_PROFILE:
            return { ...state, profile: action.payload }
        case ACTIONS.SET_IS_PICTURE_UPLOAD_LOADING:
            return { ...state, isPictureUploadLoading: action.payload }
        default:
            throw new Error("Unknown action.type " + action.type);
    }
}



export default function Index() {

    const [ , setPageTitle] = useOutletContext()

    const [ state, dispatch ] = useReducer(reducer, initialState)
    const getProfile = useGetProfile()
    const updateProfile = useUpdateProfile()
    const profileUploadPicture = useProfileUploadPicture()
    const profileDeletePicture = useProfileDeletePicture()

    const navigate = useNavigate()


    useEffect(() => {
        setPageTitle("Mon profil")
    }, [setPageTitle])



    useEffect(() => {
        const asynGetProfile = async () => {
            dispatch({ type: ACTIONS.SET_IS_LOADING, payload: true });
            dispatch({ type: ACTIONS.SET_ERROR_MESSAGE, payload: null });
            try {
                const profile = await getProfile()

                dispatch({ type: ACTIONS.SET_PROFILE, payload: profile })
                dispatch({ type: ACTIONS.SET_INITIAL_API_PAYLOAD, payload: {
                    first_name: profile.first_name,
                    last_name: profile.last_name,
                    email: profile.email,
                    email_digest: profile.email_digest || "",
                    password: "",
                    password_confirm: "",
                    digest_frequency: profile.digest_frequency,
                    digest_hour: profile.digest_hour,
                    digest_day: profile.digest_day,
                    digest_include_viewed: profile.digest_include_viewed,
                    whoiswho_hide_picture: profile.whoiswho_hide_picture,
                    whoiswho_hide_email: profile.whoiswho_hide_email,
                } })
            } catch (error) {
                console.error('profile::getProfile::error', error)
                if (isAxiosError(error)) {
                    dispatch({ type: ACTIONS.SET_ERROR_MESSAGE, payload: axiosErrorToStringError(error) });
                } else {
                    dispatch({ type: ACTIONS.SET_ERROR_MESSAGE, payload: error?.message || "Erreur inconnue" });
                }
            } finally {
                dispatch({ type: ACTIONS.SET_IS_LOADING, payload: false });
            }
        }

        asynGetProfile()
    }, [getProfile])



    /* Gestion des changements de champs du formulaire */
    const handleChange = (e) => {
        dispatch({ type: ACTIONS.SET_ERROR_MESSAGE, payload: null });
        if (e.target.type == 'checkbox') {
            dispatch({ type: ACTIONS.SET_API_PAYLOAD_FIELD, subtype: e.target.name, payload: e.target.checked })
        } else {
            dispatch({ type: ACTIONS.SET_API_PAYLOAD_FIELD, subtype: e.target.name, payload: e.target.value })
        }
    }



    /* Gestion du submit du formulaire */
    const handleSubmit = async (e) => {
        e.preventDefault()

        if ( (state.apiPayload.password || state.apiPayload.password_confirm) && 
             (state.apiPayload.password != state.apiPayload.password_confirm)
        ) {
            dispatch({ type: ACTIONS.SET_ERROR_MESSAGE, payload: "Les deux mots de passe doivent être identiques" });
            return 
        }

        // If trimmed value for first_name, last_name or email are empty, we show an error message
        if ( state.apiPayload.first_name.trim().length == 0 || state.apiPayload.last_name.trim().length == 0 || state.apiPayload.email.trim().length == 0 ) {
            dispatch({ type: ACTIONS.SET_ERROR_MESSAGE, payload: "Veuillez remplir tous les champs obligatoires" });
            return 
        }

        // If hour is outside [0, 23], we show an error message
        if ( state.apiPayload.digest_hour < 0 || state.apiPayload.digest_hour > 23 ) {
            dispatch({ type: ACTIONS.SET_ERROR_MESSAGE, payload: "L'heure doit être comprise entre 0 et 23" });
            return 
        }

        const payload = Object.fromEntries(Object.entries(state.apiPayload).filter(([key, value]) => {
            return value != state.initialApiPayload[key]
        }))

        if (Object.keys(payload).length == 0) {
            dispatch({ type: ACTIONS.SET_ERROR_MESSAGE, payload: "Aucun changement n'a été fait" });
            return 
        }

        dispatch({ type: ACTIONS.SET_IS_LOADING, payload: true });

        try {
            await updateProfile(payload)
            navigate(location.state?.from ?? "/")
        } catch (error) {
            console.error('profile::handleSubmit::error', error)
            if (error?.response?.status === 412) {
                dispatch({ type: ACTIONS.SET_ERROR_MESSAGE, payload: "Erreur de validation en base de données (username ou email déjà utilisé, contrainte non respectée)" });
            } else if (isAxiosError(error)) {
                dispatch({ type: ACTIONS.SET_ERROR_MESSAGE, payload: axiosErrorToStringError(error) });
            } else {
                dispatch({ type: ACTIONS.SET_ERROR_MESSAGE, payload: error?.message || "Erreur inconnue" });
            }
        }
    }



    /* Gestion du changement de photo */
    const handleChangePicture = async (file) => {
        dispatch({ type: ACTIONS.SET_IS_PICTURE_UPLOAD_LOADING, payload: true });
        try {
            await profileUploadPicture(file)
            const profile = await getProfile()
            dispatch({ type: ACTIONS.SET_PROFILE, payload: profile })
        } catch (error) {
            console.error('profile::handleChangePicture::error', error)
            if (isAxiosError(error)) {
                dispatch({ type: ACTIONS.SET_ERROR_MESSAGE, payload: axiosErrorToStringError(error) });
            } else {
                dispatch({ type: ACTIONS.SET_ERROR_MESSAGE, payload: error?.message || "Erreur inconnue" });
            }
        } finally {
            dispatch({ type: ACTIONS.SET_IS_PICTURE_UPLOAD_LOADING, payload: false });
        }
    }



    /* Gestion de la suppression de la photo */
    const handleDeletePicture = async () => {
        try {
            dispatch({ type: ACTIONS.SET_IS_PICTURE_UPLOAD_LOADING, payload: true });
            await profileDeletePicture()
            const profile = await getProfile()
            dispatch({ type: ACTIONS.SET_PROFILE, payload: profile })
        } catch (error) {
            console.error('profile::handleDeletePicture::error', error)
            if (isAxiosError(error)) {
                dispatch({ type: ACTIONS.SET_ERROR_MESSAGE, payload: axiosErrorToStringError(error) });
            } else {
                dispatch({ type: ACTIONS.SET_ERROR_MESSAGE, payload: error?.message || "Erreur inconnue" });
            }
        } finally {
            dispatch({ type: ACTIONS.SET_IS_PICTURE_UPLOAD_LOADING, payload: false });
        }
    }





    if (!state.profile) {
        return ""
    }


    return (
    <div className="w-full flex flex-row justify-items-start content-center gap-16 flex-wrap">
        
        {/* A Gauche, photo et informations */}
        <div className="flex items-center md:flex-col flex-row gap-5">
            {/* Photo */}
            {state?.profile?.picture_url ? 
                <img src={apiURL + state?.profile?.picture_url} alt="Photo de l'utilisateur" className="h-36 w-36 rounded-full border-2 border-secondary" 
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={async (e) => {
                            e.preventDefault();
                            if (e.dataTransfer.files.length > 0) {
                                    await handleChangePicture(e.dataTransfer.files[0]);
                            }
                    }} />
                : <div className="h-36 w-36 rounded-full border-2 border-secondary flex items-center justify-center bg-red-200 text-6xl"
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={async (e) => {
                            e.preventDefault();
                            if (e.dataTransfer.files.length > 0) {
                                    await handleChangePicture(e.dataTransfer.files[0]);
                            }
                }} 
                
                >
                    {toInitials(state?.profile?.first_name,state?.profile?.last_name)}
                </div>
            }
            {
                state?.isPictureUploadLoading ? <ClipLoader size={14} color="#696969" />
                : <div className="flex flex-row gap-2 items-center">
                    <label htmlFor="picture">
                        <span className="text-blue-500 text-sm underline hover:text-blue-800 cursor-pointer">Modifier</span>
                        <input type="file" className="hidden" id="picture" onChange={async (e) => {
                            if (e.target.files.length > 0) {
                                await handleChangePicture(e.target.files[0]);
                            }
                        }}/>
                    </label>
                    {state?.profile?.picture && <button className="text-sm text-red-500 hover:text-red-800 cursor-pointer" onClick={(e) => handleDeletePicture(e)}><Trash size={12} /></button>}
                </div>
            }
            
            

            <div className="flex flex-col gap-5">
                <div className="flex flex-col gap-1">
                    <span className="text-gray-400 text-sm">
                        Date de création : 
                    </span>
                    <span className="text-gray-400 text-sm">
                        {dayjs.utc(state.profile.creation_date).local().format('LLL')}
                    </span>
                </div>

                <div className="flex flex-col gap-1">
                    <span className="text-gray-400 text-sm">
                        Dernier résumé : 
                    </span>
                    <span className="text-gray-400 text-sm">
                        {dayjs.utc(state.profile.last_digest).local().format('LLL')}
                    </span>
                </div>
            </div>
        </div>


        <div className="flex flex-col gap-5 flex-grow max-w-2xl">

            {/* Données personnelles */}
            <div className="flex flex-row gap-5 items-center">
                <h2 className="text-secondary text-lg">Données personnelles</h2>
                { state.isLoading && <BarLoader size={20} color="#696969" /> }
            </div>
            
            <div className="flex flex-col md:flex-row gap-5 content-stretch">
                <div className="flex flex-col gap-2 flex-1">
                    <label className="text-primary text-base" htmlFor="first_name">
                        Prénom <span className="text-red-500">*</span>
                    </label>
                    <input type="text" 
                        className={`w-full border ${ state?.apiPayload?.first_name != state?.initialApiPayload?.first_name ? "border-red-600" : "border-secondary" } rounded-md`}
                        id="first_name" name="first_name" 
                        value={state.apiPayload.first_name} 
                        onChange={handleChange}
                        />
                </div>
                <div className="flex flex-col gap-2 flex-1">
                    <label className="text-primary text-base" htmlFor="last_name">
                        Nom <span className="text-red-500">*</span>
                    </label>
                    <input type="text" 
                        className={`w-full border ${ state?.apiPayload?.last_name != state?.initialApiPayload?.last_name ? "border-red-600" : "border-secondary" } rounded-md`}
                        id="last_name" name="last_name" 
                        value={state.apiPayload.last_name} 
                        onChange={handleChange}
                        />
                </div>
            </div>
            
            <div className="flex flex-col gap-2 ">
                <label className="text-primary text-base" htmlFor="last_name">
                    Email {state.profile.on_inscriptions_plateform && <span className="italic">(Ne peut pas être modifié car vous êtes rattaché à la plateforme d&apos;inscription)</span>} <span className="text-red-500">*</span>
                </label>
                <input type="text" 
                    className={`
                        w-full border ${ state?.apiPayload?.email != state?.initialApiPayload?.email ? "border-red-600" : "border-secondary" } rounded-md
                        ${state.profile.on_inscriptions_plateform ? "cursor-not-allowed bg-gray-300" : ""}
                    `}
                    id="email" name="email" 
                    value={state.apiPayload.email} 
                    onChange={handleChange}
                    disabled={state.profile.on_inscriptions_plateform}
                    />
            </div>

            <div className="flex flex-col gap-2 ">
                <label className="text-primary text-base" htmlFor="last_name">
                    Email pour les résumés
                </label>
                <input type="text" 
                    className={`
                        w-full border ${ state?.apiPayload?.email_digest != state?.initialApiPayload?.email_digest ? "border-red-600" : "border-secondary" } rounded-md
                    `}
                    id="email_digest" name="email_digest" 
                    value={state.apiPayload.email_digest} 
                    onChange={handleChange}
                    />
            </div>

            <div className="flex flex-row gap-2 ">
                <input type="checkbox" 
                    className={`w-5 h-5 border ${ state?.apiPayload?.whoiswho_hide_picture != state?.initialApiPayload?.whoiswho_hide_picture ? "border-red-600" : "border-secondary" } rounded-md`}
                    id="whoiswho_hide_picture" name="whoiswho_hide_picture" 
                    checked={state.apiPayload.whoiswho_hide_picture} 
                    onChange={handleChange}
                />
                <label className="text-primary text-base" htmlFor="whoiswho_hide_picture">
                    Je ne souhaite pas que ma photo de profil soit visible dans le Who Is Who
                </label>
            </div>

            <div className="flex flex-row gap-2 ">
                <input type="checkbox" 
                    className={`w-5 h-5 border ${ state?.apiPayload?.whoiswho_hide_email != state?.initialApiPayload?.whoiswho_hide_email ? "border-red-600" : "border-secondary" } rounded-md`}
                    id="whoiswho_hide_email" name="whoiswho_hide_email" 
                    checked={state.apiPayload.whoiswho_hide_email} 
                    onChange={handleChange}
                />
                <label className="text-primary text-base" htmlFor="whoiswho_hide_email">
                    Je ne souhaite pas que mon email soit visible dans le Who Is Who
                </label>
                
            </div>

            {/* Mot de passe */}
            <h2 className="text-secondary text-lg">Gestion du mot de passe</h2>
            { state.profile.on_keycloak && <span className='italic text-sm'>Changer le mot de passe le changera pour d&apos;autres applications internes Worldskills (Victoire)</span> }

            <div className="flex flex-col md:flex-row gap-5 content-stretch  flex-wrap">
                <div className="flex flex-col gap-2 flex-1">
                    <label className="text-primary text-base" htmlFor="password">
                        Nouveau mot de passe
                    </label>
                    <input type="password" 
                        className={`w-full border ${ state?.apiPayload?.password != state?.initialApiPayload?.password ? "border-red-600" : "border-secondary" } rounded-md`}
                        id="password" name="password" 
                        value={state.apiPayload.password} 
                        onChange={handleChange}
                        required
                        />
                </div>
                <div className="flex flex-col gap-2 flex-1">
                    <label className="text-primary text-base" htmlFor="password_confirm">
                        Confirmation mot de passe
                    </label>
                    <input type="password" 
                        className={`w-full border ${ state?.apiPayload?.password_confirm != state?.initialApiPayload?.password_confirm ? "border-red-600" : "border-secondary" } rounded-md`}
                        id="password_confirm" name="password_confirm" 
                        value={state.apiPayload.password_confirm} 
                        onChange={handleChange}
                        required
                        />
                </div>
            </div>

            {/* Préférences de résumé */}
            <h2 className="text-secondary text-lg">Préférences de résumé</h2>

            <div className="flex flex-col md:flex-row gap-5 content-stretch  flex-wrap">
                <div className="flex flex-col gap-2 flex-1">
                    <label className="text-primary text-base" htmlFor="digest_frequency">
                        Fréquence de résumé
                    </label>
                    <select 
                        className={`w-full border ${ state?.apiPayload?.digest_frequency != state?.initialApiPayload?.digest_frequency ? "border-red-600" : "border-secondary" } rounded-md`}
                        id="digest_frequency" name="digest_frequency" 
                        value={state.apiPayload.digest_frequency} 
                        onChange={handleChange}
                        required
                    >
                        <option value="hourly">Toutes les heures</option>
                        <option value="daily">Tous les jours</option>
                        <option value="weekly">Toutes les semaines</option>
                        <option value="never">Jamais</option>
                    </select>
                </div>
                { ["daily", "weekly"].includes(state?.apiPayload?.digest_frequency) && (
                    <div className="flex flex-col gap-2 flex-1">
                        <label className="text-primary text-base" htmlFor="digest_hour">
                            Heure de résumé
                        </label>
                        <input type="number" 
                            className={`w-full border ${ state?.apiPayload?.digest_hour != state?.initialApiPayload?.digest_hour ? "border-red-600" : "border-secondary" } rounded-md`}
                            id="digest_hour" name="digest_hour" 
                            value={state.apiPayload.digest_hour} 
                            onChange={handleChange}
                            required
                            />
                    </div>
                )}
                { ["weekly"].includes(state?.apiPayload?.digest_frequency) && (
                    <div className="flex flex-col gap-2  flex-1">
                        <label className="text-primary text-base" htmlFor="digest_day">
                            Jour de résumé
                        </label>
                        <select className="w-full border border-secondary rounded-md" 
                            id="digest_day" name="digest_day" 
                            value={state.apiPayload.digest_day} 
                            onChange={handleChange}
                            required
                        >
                            <option value="0">Lundi</option>
                            <option value="1">Mardi</option>
                            <option value="2">Mercredi</option>
                            <option value="3">Jeudi</option>
                            <option value="4">Vendredi</option>
                            <option value="5">Samedi</option>
                            <option value="6">Dimanche</option>
                        </select>
                    </div>
                )}
            </div>
            <div className="flex flex-row gap-2 items-center">
                
                <input type="checkbox" 
                    className={`w-5 h-5 border ${ state?.apiPayload?.digest_include_viewed != state?.initialApiPayload?.digest_include_viewed ? "border-red-600" : "border-secondary" } rounded-md`}
                    id="digest_include_viewed" name="digest_include_viewed" 
                    checked={state.apiPayload.digest_include_viewed} 
                    onChange={handleChange}
                    />
                <label className="text-primary text-base" htmlFor="digest_include_viewed">
                    Inclure les sujets vus
                </label>
            </div>

            <div className="w-[50%] gap-5 flex flex-row items-center">
                
                <button className="flex gap-2 items-center border border-blue-500 rounded-md px-2 py-1 text-base hover:bg-blue-200"
                    onClick={(e) => handleSubmit(e)}
                    disabled={state.isLoading}
                >
                    <Save size={24} className="text-primary" />
                    Sauvegarder
                </button>
                
                {
                    state?.errorMessage && <span className="text-red-700 flex-none content-center mr-4">{state.errorMessage}</span>
                }
            </div>

        </div>            
    </div>
    )
}